import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import "./styles/DataCenterServices.css";

const serviceTabs = [
  {
    key: "migration",
    label: "Migration & Deployment",
    title: "Move, install, and maintain infrastructure with less disruption",
    text: "Support data center migration and relocation projects, hardware installation, and ongoing maintenance with a structured service approach built around uptime and operational continuity.",
    points: [
      "Migration and relocation planning",
      "Rack, stack, and hardware deployment",
      "Ongoing hardware installation and maintenance",
    ],
  },
  {
    key: "resilience",
    label: "Backup & Continuity",
    title: "Protect data and keep business operations moving",
    text: "Build resilience through backup, archiving, managed load balancing, and managed business continuance services that help reduce disruption and support service availability.",
    points: [
      "Data backup and archiving",
      "Managed load balancing",
      "Managed business continuance",
    ],
  },
  {
    key: "security",
    label: "Access & Security",
    title: "Control access while strengthening your operating environment",
    text: "Use controlled internet access, managed authentication and authorization, and layered firewall plus anti malware services to support a more secure data center environment.",
    points: [
      "Controlled internet access",
      "Managed user authentication and authorization",
      "Diverse firewalls and anti malware programs",
    ],
  },
  {
    key: "messaging",
    label: "User Services",
    title: "Support day to day communication and managed platform services",
    text: "Managed e-mail and messaging services help keep internal communication flowing while fitting into broader infrastructure and continuity planning.",
    points: [
      "Managed e-mail and messaging",
      "User facing platform support",
      "Operational service coordination",
    ],
  },
];

const operationsCards = [
  {
    title: "Infrastructure changes",
    text: "Handle moves, installs, maintenance cycles, and equipment updates with more structure and operational control.",
  },
  {
    title: "Availability support",
    text: "Use backup, load balancing, and continuity planning to reduce risk around outages and service interruptions.",
  },
  {
    title: "Access governance",
    text: "Control who can reach systems and services through managed authentication, authorization, and internet access policies.",
  },
  {
    title: "Security layers",
    text: "Support data center protection with firewalls, anti malware, and managed controls across the environment.",
  },
];

const checklistGroups = [
  {
    heading: "Core infrastructure services",
    items: [
      "Migration and relocation support",
      "Hardware installation and maintenance",
      "Managed load balancing",
      "Controlled internet access",
    ],
  },
  {
    heading: "Protection and continuity services",
    items: [
      "Data backup and archiving",
      "Managed business continuance",
      "Firewall and anti malware services",
      "Managed authentication and authorization",
    ],
  },
  {
    heading: "Operational user services",
    items: [
      "Managed e-mail and messaging",
      "Service coordination around day to day operations",
      "Support aligned to business requirements",
      "Infrastructure tailored to company needs",
    ],
  },
];

const fitCards = [
  {
    title: "Businesses modernizing infrastructure",
    text: "For teams that need help relocating, refreshing, or better managing data center environments.",
  },
  {
    title: "Operations that need higher resilience",
    text: "For organizations that want continuity, backup, and availability services built into daily infrastructure operations.",
  },
  {
    title: "Security conscious IT environments",
    text: "For businesses that need stronger access control, internet governance, and protective layers across systems.",
  },
];

export default function DataCenterServices() {
  const [activeTab, setActiveTab] = useState(serviceTabs[0]);

  const tabButtons = useMemo(
    () =>
      serviceTabs.map((tab) => (
        <button
          key={tab.key}
          type="button"
          className={`dcTabBtn ${activeTab.key === tab.key ? "active" : ""}`}
          onClick={() => setActiveTab(tab)}
        >
          {tab.label}
        </button>
      )),
    [activeTab]
  );

  return (
    <div className="dcPage">
      <section className="dcHero">
        <div className="dcContainer dcHero__container">
          <div className="dcHero__content">
            <p className="dcEyebrow">Data Center Services</p>

            <h1 className="dcHero__title">
              Managed data center support built around continuity, control, and daily operations.
            </h1>

            <p className="dcHero__text">
              ComNetworks delivers data center services tailored to your company’s
              requirements, including migration and relocation, hardware
              installation and maintenance, backup and archiving, load balancing,
              internet access control, managed messaging, user authentication,
              firewall and anti malware protection, and business continuance.
            </p>

            <div className="dcHero__actions">
              <Link to="/contact" className="dcBtn dcBtn--primary">
                Talk to Our Team
              </Link>
              <Link to="/services" className="dcBtn dcBtn--secondary">
                Explore Services
              </Link>
            </div>
          </div>

          <div className="dcHero__visual">
            <div className="dcPanel">
              <div className="dcPanel__top">
                <span className="dcMiniTag">Managed services</span>
                <span className="dcMiniTag dcMiniTag--soft">Business continuity</span>
              </div>

              <div className="dcPanel__screen">
                <div className="dcRackColumn">
                  <span className="dcRackUnit" />
                  <span className="dcRackUnit" />
                  <span className="dcRackUnit" />
                  <span className="dcRackUnit" />
                </div>

                <div className="dcOpsColumn">
                  <div className="dcOpsCard">
                    <strong>Backup</strong>
                    <span>Archiving and recovery support</span>
                  </div>
                  <div className="dcOpsCard">
                    <strong>Security</strong>
                    <span>Firewall and anti-malware layers</span>
                  </div>
                  <div className="dcOpsCard">
                    <strong>Access</strong>
                    <span>Authentication and authorization control</span>
                  </div>
                </div>
              </div>

              <div className="dcPanel__bottom">
                <div className="dcSignal">
                  <strong>Infrastructure state</strong>
                  <span>Migration, maintenance, and managed operations aligned</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="dcTabsSection">
        <div className="dcContainer">
          <div className="dcSectionHead dcSectionHead--center">
            <p className="dcEyebrow">Service groups</p>
            <h2 className="dcTitle">
              Explore the service set through operational categories
            </h2>
            <p className="dcText">
              Instead of listing everything in one long block, this layout
              groups services by how businesses actually manage infrastructure,
              continuity, access, and support.
            </p>
          </div>

          <div className="dcTabsWrap">
            <div className="dcTabsNav">{tabButtons}</div>

            <div className="dcTabsPanel">
              <div className="dcTabsPanel__content">
                <p className="dcTabsLabel">{activeTab.label}</p>
                <h3>{activeTab.title}</h3>
                <p>{activeTab.text}</p>
              </div>

              <div className="dcTabsPanel__list">
                {activeTab.points.map((point) => (
                  <div key={point} className="dcPointItem">
                    <span className="dcPointItem__dot" />
                    <span>{point}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="dcOperationsSection">
        <div className="dcContainer dcOperationsWrap">
          <div className="dcOperationsIntro">
            <p className="dcEyebrow">Operational coverage</p>
            <h2 className="dcTitle">
              A page structure focused on what your team manages every day
            </h2>
            <p className="dcText">
              Data center support is often wider than one project or one tool.
              It spans infrastructure work, availability, access control, and
              managed protection across the environment.
            </p>
          </div>

          <div className="dcOperationsGrid">
            {operationsCards.map((item) => (
              <article key={item.title} className="dcOperationsCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dcChecklistSection">
        <div className="dcContainer">
          <div className="dcChecklistBoard">
            {checklistGroups.map((group) => (
              <article key={group.heading} className="dcChecklistCard">
                <h3>{group.heading}</h3>
                <div className="dcChecklistItems">
                  {group.items.map((item) => (
                    <div key={item} className="dcChecklistItem">
                      <span className="dcChecklistItem__icon">✓</span>
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dcContinuitySection">
        <div className="dcContainer">
          <div className="dcContinuityBand">
            <div className="dcContinuityBand__left">
              <p className="dcEyebrow">Continuity focus</p>
              <h2 className="dcTitle">
                Backup, messaging, access, and protection all support business continuity
              </h2>
            </div>

            <div className="dcContinuityBand__right">
              <p className="dcText">
                The value of managed data center services is not only technical
                support. It is also the ability to keep business operations more
                stable through coordinated backup, communications, access
                control, security, and continuity planning.
              </p>
            </div>
          </div>
        </div>
      </section>

      <section className="dcFitSection">
        <div className="dcContainer">
          <div className="dcSectionHead dcSectionHead--center">
            <p className="dcEyebrow">Best fit</p>
            <h2 className="dcTitle">
              Where Data Center Services create the most value
            </h2>
            <p className="dcText">
              These services are especially useful where uptime, operational
              discipline, and managed support matter across a broad set of
              infrastructure needs.
            </p>
          </div>

          <div className="dcFitGrid">
            {fitCards.map((item) => (
              <article key={item.title} className="dcFitCard">
                <h3>{item.title}</h3>
                <p>{item.text}</p>
              </article>
            ))}
          </div>
        </div>
      </section>

      <section className="dcCta">
        <div className="dcContainer dcCta__container">
          <div className="dcCta__content">
            <p className="dcEyebrow dcEyebrow--light">Next step</p>
            <h2 className="dcCta__title">
              Build a managed data center approach around your real operating needs
            </h2>
            <p className="dcCta__text">
              If your business needs help with migration, maintenance, backup,
              security, messaging, authentication, and business continuity,
              ComNetworks can help organize the right service model.
            </p>
          </div>

          <div className="dcCta__actions">
            <Link to="/contact" className="dcBtn dcBtn--primary">
              Contact Us
            </Link>
            <Link to="/services" className="dcBtn dcBtn--ghost">
              View More Services
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
